import { useCallback, useEffect, useRef, useState } from 'react';

export type SwapDir = 'next' | 'prev';

/**
 * Vrai dès que l'élément entre dans l'écran (une seule fois).
 * L'élément est suivi par un état (et non un ref): il peut n'apparaître qu'après le chargement des données.
 */
export function useSeen<T extends HTMLElement>() {
  const [el, setEl] = useState<T | null>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (!el || seen) return;
    if (typeof IntersectionObserver === 'undefined') { setSeen(true); return; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setSeen(true); io.disconnect(); } }, { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, [el, seen]);
  return [setEl, seen] as const;
}

/**
 * État d'un jeu d'onglets animé: onglet courant, onglet qui sort, sens du glissement.
 * `revealed` passe à vrai au premier changement, pour ne jouer le dévoilement initial qu'une fois.
 */
export function useTabSwap(ids: string[], swapMs = 1500) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [prevId, setPrevId] = useState<string | null>(null);
  const [dir, setDir] = useState<SwapDir>('next');
  const [revealed, setRevealed] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const currentId = activeId && ids.includes(activeId) ? activeId : ids[0];

  const select = useCallback((id: string) => {
    if (id === currentId) return;
    setDir(ids.indexOf(id) > ids.indexOf(currentId) ? 'next' : 'prev');
    setRevealed(true);
    setPrevId(currentId);
    setActiveId(id);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setPrevId(null), swapMs);
  }, [currentId, ids, swapMs]);

  return { currentId, prevId, dir, revealed, swapping: prevId !== null, select };
}
